<i18n src="./resources/locales.json"></i18n>

<template>
  <div>
    <v-menu
      :value="show"
      :position-x="position.x"
      :position-y="position.y"
      :z-index="1000"
      :close-on-click="false"
      :close-on-content-click="false"
      absolute
      offset-y
    >
      <v-card @mouseenter.stop="keepOpen" @mouseleave.stop="syncClose">
        <v-list>
          <v-list-item
            v-for="(item, index) in actions"
            :key="index"
            :disabled="isOfflineModeEnabled || item.disabled"
            @click="item.action"
          >
            <v-list-item-title :style="{ color: item.customColor ? item.customColor : 'inherit' }">
              {{ $t(item.label) }}
            </v-list-item-title>
          </v-list-item>
          <v-divider v-show="showDivider" class="divider" />
          <v-list-item
            v-if="primaryAction"
            :disabled="isItemDisabled"
            class="primary-action"
            @click="primaryAction.action"
          >
            <v-list-item-icon class="mr-0">
              <v-icon small>{{ primaryAction.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t(primaryAction.label) }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>

    <!-- Dialogs -->
    <EcsContactsSelectionDialog ref="contactSelectionDialog" />
    <EcsConfirmationDialog
      ref="addToDncConfirmationDialog"
      :title="$t('dialogs.addToDNCListDialog.title')"
      :message="$t('dialogs.addToDNCListDialog.content')"
      :confirm-button-text="$t('dialogs.addToDNCListDialog.acceptBtn')"
      :cancel-button-text="$t('dialogs.addToDNCListDialog.cancelBtn')"
    />
    <EcsConfirmationDialog
      ref="removeFromDncConfirmationDialog"
      :title="$t('dialogs.removeFromDNCListDialog.title')"
      :message="$t('dialogs.removeFromDNCListDialog.content')"
      :confirm-button-text="$t('dialogs.removeFromDNCListDialog.acceptBtn')"
      :cancel-button-text="$t('dialogs.removeFromDNCListDialog.cancelBtn')"
    />
    <EcsGroupChatSetupDialog ref="groupChatSetupDialog" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EcsContactsSelectionDialog from '@/components/ecs-contacts-selection-dialog/EcsContactsSelectionDialog';
import EcsConfirmationDialog from '@/components/ecs-confirmation-dialog/EcsConfirmationDialog';
import EcsGroupChatSetupDialog from '@/components/ecs-group-chat-setup-dialog/EcsGroupChatSetupDialog';
import EcsInteractionActionsMixin from '@/mixins/ecs-interaction-mixin/EcsInteractionActionsMixin';
import { INTERACTION_STATE } from '@/constants/interaction/constants';

export default {
  name: 'EcsInteractionContextMenu',

  components: { EcsGroupChatSetupDialog, EcsContactsSelectionDialog, EcsConfirmationDialog },

  mixins: [EcsInteractionActionsMixin],

  data() {
    return {
      position: {
        x: null,
        y: null
      },
      show: false,
      closeTimeoutRef: null,
      interaction: null
    };
  },

  computed: {
    ...mapGetters('SETTINGS', ['isOfflineModeEnabled']),

    actions() {
      if (!this.interaction) {
        return [];
      }

      let result;
      if (this.interaction.interactionStateId === INTERACTION_STATE.offered) {
        result = this.getActionsOnPending();
      } else if (this.interaction.isHistoric) {
        result = this.getActionsOnHistoric();
      } else {
        result = this.getActionsOnActive();
      }

      return result;
    },

    primaryAction() {
      let result;

      if (!this.interaction || this.interaction.interactionStateId === INTERACTION_STATE.offered) {
        return result;
      }

      if (this.interaction.isHistoric) {
        // TODO: Temporarily removed until ECS can provide a delete historic interaction feature
        result = null;
      } else if (this.interaction.isOnWrapUp) {
        result = this.primaryOptions.complete;
      } else if (this.interaction.isEmail) {
        const email = this.getEmailByInteractionId(this.interaction.interactionId); // NOTE: Review on detail
        result = email.isReplying ? this.primaryOptions.send : this.primaryOptions.reply;
      } else {
        result = this.primaryOptions.end;
      }

      return result;
    },

    showDivider() {
      return this.actions.length && this.primaryAction;
    },

    isItemDisabled() {
      return this.isOfflineModeEnabled || this.primaryAction.disabled;
    }
  },

  created() {
    this.$root.$on('open-interaction-context-menu', this.open);
    this.$root.$on('close-interaction-context-menu', this.close);
  },

  beforeDestroy() {
    this.$root.$off('open-interaction-context-menu', this.open);
    this.$root.$off('close-interaction-context-menu', this.close);
  },

  methods: {
    keepOpen() {
      clearTimeout(this.closeTimeoutRef);
    },

    open({ interaction, x, y }) {
      this.interaction = interaction;
      this.position.x = x;
      this.position.y = y;
      this.show = true;
      clearTimeout(this.closeTimeoutRef);
    },

    syncClose() {
      clearTimeout(this.closeTimeoutRef);
      this.show = false;
    },

    close() {
      this.closeTimeoutRef = setTimeout(() => {
        this.show = false;
      }, 300);
    }
  }
};
</script>

<style scoped>
.divider {
  background-color: var(--v-primary-base);
}
.primary-action .v-list-item__icon .v-icon,
.primary-action .v-list-item__content .v-list-item__title {
  color: var(--v-primary-base);
}
</style>
